
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import PaymentProcessor from "@/components/payment/PaymentProcessor";
import LoginRequiredDialog from "@/components/courses/LoginRequiredDialog";
import PaymentStatusDialog from "@/components/payment/PaymentStatusDialog";
import { checkPaymentStatus } from "@/api/check-payment-status";

interface CoursePlayerProps {
  courseId: string;
  courseName: string;
  coursePrice: string;
  lessons?: any[];
}

type PaymentSettings = {
  enabled: boolean;
  currency: string;
  paymentMethodsEnabled: {
    card: boolean;
    wallet: boolean;
  };
};

const CoursePlayer = ({ courseId, courseName, coursePrice, lessons = [] }: CoursePlayerProps) => {
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isPaymentRequired, setIsPaymentRequired] = useState(true);
  const [courseLessons, setCourseLessons] = useState<any[]>(lessons);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({ 
    enabled: true, 
    currency: "EGP",
    paymentMethodsEnabled: {
      card: true,
      wallet: true,
    }
  });
  const [paymentStatusDialog, setPaymentStatusDialog] = useState<{
    isOpen: boolean;
    status: "success" | "failed" | "pending" | null;
    message?: string;
  }>({
    isOpen: false,
    status: null
  });
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const urlPaymentStatus = searchParams.get('status');

  useEffect(() => {
    const status = searchParams.get('status');
    
    if (status === 'success' || status === 'failed' || status === 'pending') {
      setPaymentStatusDialog({
        isOpen: true,
        status: status as "success" | "failed" | "pending",
        message: status === 'success' 
          ? `You've successfully enrolled in ${courseName}! You'll be redirected to your lessons.` 
          : status === 'failed'
          ? "Your payment couldn't be processed. Please try again."
          : "Your payment is being processed. This may take a moment."
      });
      
      // Clear URL parameters
      setSearchParams({});
      
      // If payment was successful, check enrollment status again and redirect after a short delay
      if (status === 'success') {
        handleSuccessfulPayment();
        
        // Auto-redirect to lessons page after a short delay
        setTimeout(() => {
          handleStartLearning();
        }, 2500);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (lessons.length > 0) {
      setCourseLessons(lessons);
    } else {
      fetchLessons();
    }
    fetchPaymentSettings();
  }, [courseId, lessons]);

  useEffect(() => {
    if (user) {
      checkEnrollment();
      checkPaymentStatus();
    } else {
      setIsEnrolled(false);
      setIsPaid(false);
    }
  }, [user, courseId]);

  const fetchLessons = async () => {
    if (!courseId) return;
    
    try {
      const { data, error } = await supabase
        .from('course_lessons')
        .select('*')
        .eq('course_id', courseId)
        .order('lesson_order', { ascending: true });
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        setCourseLessons(data);
      }
    } catch (error: any) {
      console.error('Error fetching lessons:', error.message);
      toast({
        title: "Failed to load lessons",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("payment_settings")
        .select("settings")
        .limit(1)
        .single();
      
      if (error) {
        console.error('Error fetching payment settings:', error);
        return;
      }
      
      if (data && data.settings) {
        const settingsData = data.settings as PaymentSettings;
        setPaymentSettings(settingsData);
        setIsPaymentRequired(settingsData.enabled !== false);
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const checkEnrollment = async () => {
    if (!user || !courseId) return;

    try {
      const { data, error } = await supabase
        .from('course_enrollments')
        .select('*')
        .eq('course_id', courseId)
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking enrollment:', error);
        return;
      }

      setIsEnrolled(!!data);
    } catch (error) {
      console.error('Error checking enrollment:', error);
    }
  };

  const checkPaymentStatus = async () => {
    if (!user || !courseId) return;

    try {
      const { data, error } = await supabase
        .from('course_payments')
        .select('*')
        .eq('course_id', courseId)
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .maybeSingle();

      if (error) {
        console.error('Error checking payment status:', error);
        return;
      }

      setIsPaid(!!data);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSuccessfulPayment = async () => {
    if (!user || !courseId) return;

    try {
      // First check if payment record already exists
      const { data: existingPayment, error: checkError } = await supabase
        .from('course_payments')
        .select('id')
        .eq('user_id', user.id)
        .eq('course_id', courseId)
        .eq('status', 'completed')
        .maybeSingle();
        
      if (checkError) {
        console.error('Error checking payment:', checkError);
      }
      
      // Only create payment record if one doesn't exist
      if (!existingPayment) {
        const { error: paymentError } = await supabase
          .from('course_payments')
          .insert({
            user_id: user.id,
            course_id: courseId,
            amount: parseFloat(coursePrice.replace(/[^0-9.]/g, '')),
            status: 'completed'
          });

        if (paymentError) {
          console.error('Error recording payment:', paymentError);
        }

        const { error: updateError } = await supabase
          .from('course_payment_attempts')
          .update({ status: 'completed' })
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .eq('status', 'pending');

        if (updateError) {
          console.error('Error updating payment attempt:', updateError);
        }
      }

      setIsPaid(true);
      
      // Handle enrollment
      await handleEnroll(true);
      
      // Clear URL parameters
      const url = new URL(window.location.href);
      if (url.searchParams.has('status')) {
        url.searchParams.delete('status');
        window.history.replaceState({}, document.title, url.toString());
      }
      
    } catch (error: any) {
      console.error('Error processing successful payment:', error);
    }
  };

  const triggerEnrollmentWebhook = async (userId: string, courseId: string) => {
    try {
      const { error } = await supabase.functions.invoke('make-webhook', {
        body: { userId, courseId }
      });

      if (error) {
        console.error('Error triggering webhook:', error);
      } else {
        console.log('Enrollment webhook triggered successfully');
      }
    } catch (error) {
      console.error('Exception triggering webhook:', error);
    }
  };

  const handleEnroll = async (skipEnrollmentCheck = false) => {
    if (!user) {
      setIsLoginDialogOpen(true);
      return;
    }

    if (isPaymentRequired && !isPaid && !skipEnrollmentCheck) {
      return;
    }

    // Don't process if already enrolled
    if (isEnrolled && !skipEnrollmentCheck) {
      handleStartLearning();
      return;
    }

    setIsEnrolling(true);

    try {
      // Check if already enrolled first
      if (!skipEnrollmentCheck) {
        const { data: existingEnrollment, error: checkError } = await supabase
          .from('course_enrollments')
          .select('id')
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .maybeSingle();
          
        if (checkError) {
          console.error('Error checking enrollment:', checkError);
        }
        
        // If already enrolled, just set state and return
        if (existingEnrollment) {
          setIsEnrolled(true);
          setIsEnrolling(false);
          handleStartLearning();
          return;
        }
      }
      
      // Only create a new enrollment if not already enrolled
      const { error } = await supabase
        .from('course_enrollments')
        .insert({
          user_id: user.id,
          course_id: courseId
        });

      if (error) throw error;

      setIsEnrolled(true);
      
      await triggerEnrollmentWebhook(user.id, courseId);
      
      toast({
        title: "Enrolled successfully",
        description: `You are now enrolled in ${courseName}`,
      });
      
      // Auto-navigate to lessons
      handleStartLearning();
    } catch (error: any) {
      console.error('Error enrolling in course:', error);
      toast({
        title: "Enrollment failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleStartLearning = () => {
    if (courseLessons && courseLessons.length > 0) {
      navigate(`/courses/${courseId}/lessons`);
    } else {
      toast({
        title: "No lessons available",
        description: "This course doesn't have any lessons yet.",
        variant: "destructive"
      });
    }
  };

  const closePaymentDialog = () => {
    setPaymentStatusDialog(prev => ({ ...prev, isOpen: false }));
  };

  if (!courseLessons || courseLessons.length === 0) {
    return (
      <Card className="p-6 text-center">
        <h3 className="text-xl font-semibold mb-4">No lessons available</h3>
        <p className="text-gray-500">This course doesn't have any lessons yet.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Course Content</h2>
      
      {!isEnrolled ? (
        <>
          {isPaymentRequired && !isPaid ? (
            <PaymentProcessor 
              courseId={courseId}
              courseName={courseName}
              price={coursePrice}
              onSuccess={() => {
                setIsPaid(true);
                handleEnroll();
              }}
              webhookUrl="https://fmuvniyvftmenrgeywtv.supabase.co/functions/v1/paymob-webhook"
              responseUrl="https://fmuvniyvftmenrgeywtv.supabase.co/functions/v1/paymob-response"
            />
          ) : (
            <Card className="p-8 text-center">
              <h3 className="text-xl font-semibold mb-3">Get Started with This Course</h3>
              <p className="text-gray-600 mb-6">Enroll now to access all lessons and materials</p>
              <Button 
                onClick={() => handleEnroll()}
                disabled={isEnrolling}
                className="w-full max-w-xs"
              >
                {isEnrolling ? "Processing..." : "Enroll in This Course"}
              </Button>
            </Card>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <Card className="p-8 text-center">
            <h3 className="text-xl font-semibold mb-3">You're Enrolled!</h3>
            <p className="text-gray-600 mb-6">Continue your learning journey with {courseLessons.length} lessons</p>
            <Button 
              onClick={handleStartLearning} 
              className="w-full max-w-xs"
            >
              Start Learning
            </Button>
          </Card>
        </div>
      )}
      
      <LoginRequiredDialog
        isOpen={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
        redirectUrl={`/courses/${courseId}`}
        message="Please log in to enroll in this course."
      />
      
      <PaymentStatusDialog
        isOpen={paymentStatusDialog.isOpen}
        onClose={closePaymentDialog}
        status={paymentStatusDialog.status}
        message={paymentStatusDialog.message}
      />
    </div>
  );
};

export default CoursePlayer;
